import { type RoleModel, UserRoleEnum } from 'api/roles'
import { ReferenceType, ReferenceTypeNumerable } from 'shared/models/User'

/** Board roles that count as an elected board member */
export const BOARD_MEMBER_ROLES = [
  UserRoleEnum.boardleader,
  UserRoleEnum.boardmember,
  UserRoleEnum.boardnextleader,
]

/** Every role that gives a seat in the board */
export const BOARD_ROLES = [
  ...BOARD_MEMBER_ROLES,
  UserRoleEnum.alternateboardmember,
  UserRoleEnum.secretary,
  UserRoleEnum.cashier,
  UserRoleEnum.committee,
]

/** Roles that grant access to the portal at all */
export const PORTAL_ACCESS_ROLES = [
  UserRoleEnum.portal,
  UserRoleEnum.caretaker,
  UserRoleEnum.auditor,
  UserRoleEnum.advisor,
  UserRoleEnum.advisorFullAccess,
  UserRoleEnum.digitalkeys,
  UserRoleEnum.ceo,
]

/** Roles that make a client visible to the user */
const CLIENT_ACCESS_ROLES = [
  UserRoleEnum.portal,
  UserRoleEnum.caretaker,
  UserRoleEnum.auditor,
  UserRoleEnum.digitalkeys,
]

// The API returns reference types as strings in some endpoints and as numbers in others
const isClientReference = (type: ReferenceType) =>
  type === ReferenceType.ClientId ||
  (type as unknown as ReferenceTypeNumerable) ===
    ReferenceTypeNumerable.ClientId

export const getRolesForClient = (
  roles: RoleModel[] = [],
  clientId?: string
) =>
  clientId
    ? roles.filter((role) =>
        role.reference.some(
          (ref) => isClientReference(ref.type) && ref.value === clientId
        )
      )
    : []

/** True if the user holds any of the given roles, for any client */
export const hasRole = (roles: RoleModel[] = [], ...allowed: UserRoleEnum[]) =>
  roles.some(({ role }) => allowed.includes(role))

/** True if the user holds any of the given roles for the given client */
export const hasRoleForClient = (
  roles: RoleModel[] = [],
  clientId?: string,
  ...allowed: UserRoleEnum[]
) => hasRole(getRolesForClient(roles, clientId), ...allowed)

export const isBoardRole = (role: string) =>
  BOARD_ROLES.includes(role as UserRoleEnum)

export const isStyrePortalBruker = (role: string) =>
  role === UserRoleEnum.portal

/** Requires both styreportalbruker and the advisor role for the same client */
export const hasAdvisorRolesForClient = (
  roles: RoleModel[] = [],
  clientId?: string,
  advisorRole = UserRoleEnum.advisor
) => {
  const rolesForClient = getRolesForClient(roles, clientId)

  return (
    hasRole(rolesForClient, UserRoleEnum.portal) &&
    hasRole(rolesForClient, advisorRole)
  )
}

/** True when the client grants nothing beyond styreportalbruker and the advisor role */
export const hasOnlyStyrePortalBrukerAndAdvisor = (
  roles: RoleModel[] = [],
  clientId?: string,
  advisorRole = UserRoleEnum.advisor
) => {
  const rolesForClient = getRolesForClient(roles, clientId)

  return (
    hasRole(rolesForClient, advisorRole) &&
    rolesForClient.every(
      ({ role }) => isStyrePortalBruker(role) || role === advisorRole
    )
  )
}

export const getClientIdsForUser = (userRoles: RoleModel[] = []): string[] => [
  ...new Set(
    userRoles
      .filter(({ role }) => CLIENT_ACCESS_ROLES.includes(role))
      .flatMap(({ reference }) => reference.map((ref) => ref.value))
  ),
]
